/**
 * Checkpoint evaluation: every `checkpointSize` answers the window is scored
 * (accuracy, speed, consistency → performance P and confidence κ) and the hidden
 * ability rating moves toward the level where the player performs at P*.
 * Spec: docs/GAME_DESIGN.md § 3; worked examples: docs/ADAPTIVE.md.
 */
import { ADAPTIVE, LEVEL_WIDTH } from '../config.ts';
import { ratingForLevel } from '../levels/levels.ts';
import { clamp01, cv, longestRun, median } from '../math/stats.ts';
import type { AnswerRecord, CheckpointDecision, CheckpointResult, CheckpointStats } from '../types.ts';
import { clampRating, levelForRating } from './rating.ts';
import type { AdaptiveConstants } from './types.ts';

/** ramp(x; a→b) = clamp01((x − a) / (b − a)); a step at `a` when b ≤ a. */
export function ramp(x: number, a: number, b: number): number {
  if (b <= a) return x >= a ? 1 : 0;
  return clamp01((x - a) / (b - a));
}

/** Normalized response time τ = responseMs / parTimeMs. */
export function normalizedTime(record: Pick<AnswerRecord, 'responseMs' | 'parTimeMs'>): number {
  return record.parTimeMs > 0 ? record.responseMs / record.parTimeMs : 1;
}

/**
 * A pace timeout: the block locked before the player had even par time
 * (available < PACE_TIMEOUT_PAR × par). The run-long pace ramp outran them —
 * it still adds a brick (that's how runs end), but it says nothing about
 * whether the algebra is too hard, so it doesn't count against accuracy.
 */
export function isPaceTimeout(record: Pick<AnswerRecord, 'outcome' | 'responseMs' | 'parTimeMs'>, k: AdaptiveConstants = ADAPTIVE): boolean {
  return record.outcome === 'timeout' && record.responseMs < record.parTimeMs * k.PACE_TIMEOUT_PAR;
}

/** Minimum number of correct answers before timing spread (cv) says anything about consistency. */
const MIN_CORRECT_FOR_CV = 3;

export function computeCheckpointStats(window: readonly AnswerRecord[], k: AdaptiveConstants = ADAPTIVE): CheckpointStats {
  const n = window.length;
  const correctFlags = window.map((r) => r.outcome === 'correct');
  const taus = window.filter((r) => r.outcome === 'correct').map(normalizedTime);
  const correct = taus.length;
  // Accuracy judges the content, not the pace: a timeout only counts against it
  // when the block allowed at least PACE_TIMEOUT_PAR × par (see isPaceTimeout).
  const judged = window.filter((r) => !isPaceTimeout(r, k)).length;

  const A = judged > 0 ? correct / judged : 0;
  const T = correct > 0 ? median(taus) : k.T_NO_CORRECT;
  const S = clamp01((k.T_SLOW - T) / (k.T_SLOW - k.T_FAST));
  const cvPart = correct >= MIN_CORRECT_FOR_CV ? 1 - clamp01(cv(taus) / k.CV_MAX) : 0;
  const runPart = n > 0 ? longestRun(correctFlags) / n : 0;
  const C = k.C_TIME_WEIGHT * cvPart + (1 - k.C_TIME_WEIGHT) * runPart;
  // Speed and consistency only count for answers you get right (× A).
  const P = k.W_A * A + k.W_S * S * A + k.W_C * C * A;
  const confidence = ramp(A, k.CONF_A0, 1) * ramp(S, k.CONF_S0, k.CONF_S1) * ramp(C, k.CONF_C0, k.CONF_C1);
  const medianSec = median(window.map((r) => r.responseMs)) / 1000;

  return { n, A, medianSec, T, S, C, P, confidence };
}

/**
 * Under-placed: the player's correct answers come in far under par (median
 * τ ≤ T_UNDERPLACED) with at least modest accuracy. Their misses aren't the
 * content being too hard — they're careless or deliberate — so they move up.
 */
export function isUnderplaced(stats: Pick<CheckpointStats, 'A' | 'T'>, k: AdaptiveConstants = ADAPTIVE): boolean {
  return stats.T <= k.T_UNDERPLACED && stats.A >= k.A_UNDERPLACED;
}

/**
 * Normalized distance from the target: +1 at P = 1, 0 at P = P*, −1 at P = 0.
 * The mastery gate caps it at 0 when accuracy is below A_MIN_UP — unless the
 * player is under-placed, in which case it is at least D_UNDERPLACED.
 */
export function normalizedDelta(stats: Pick<CheckpointStats, 'P' | 'A' | 'T'>, k: AdaptiveConstants = ADAPTIVE): number {
  const target = k.P_TARGET;
  const d = stats.P >= target ? (stats.P - target) / (1 - target) : (stats.P - target) / target;
  if (isUnderplaced(stats, k)) return Math.max(d, k.D_UNDERPLACED);
  return stats.A < k.A_MIN_UP ? Math.min(d, 0) : d;
}

/**
 * Rating change for a normalized delta d. Upward moves are amplified by
 * confidence (κ² → only when accuracy, speed and consistency are all high);
 * downward moves are linear and capped by the MAX_DROP_LEVELS safety rail.
 */
export function ratingDelta(d: number, confidence: number, k: AdaptiveConstants = ADAPTIVE): number {
  if (d >= 0) return d * k.K_UP * (1 + (k.JUMP_MAX - 1) * confidence * confidence);
  return Math.max(d * k.K_DOWN, -k.MAX_DROP_LEVELS * LEVEL_WIDTH);
}

export function decisionFor(levelBefore: number, levelAfter: number): CheckpointDecision {
  const step = levelAfter - levelBefore;
  if (step === 0) return 'stay';
  if (step === 1) return 'up';
  if (step > 1) return 'jump-up';
  return step === -1 ? 'down' : 'jump-down';
}

/** What the run already knows about where the player's ability tops out. */
export interface CheckpointContext {
  /**
   * Jump ceiling: the lowest level at which this player has shown they are NOT
   * yet comfortable (a checkpoint in this run below target, or one level above
   * their established rating). Confidence jumps may not carry the rating to or
   * past it; at or above it the player climbs one ordinary step at a time.
   */
  ceilingLevel?: number | null;
}

/**
 * Upward rating change, limited by the jump ceiling: the confident jump is
 * allowed up to just below `ceilingLevel`, and never less than an ordinary
 * (unamplified) step.
 */
export function cappedUpDelta(d: number, confidence: number, rating: number, ceilingLevel: number | null | undefined, k: AdaptiveConstants = ADAPTIVE): number {
  const jump = ratingDelta(d, confidence, k);
  if (ceilingLevel == null) return jump;
  const plain = ratingDelta(d, 0, k);
  const room = ratingForLevel(ceilingLevel) - k.HYSTERESIS - 1 - rating;
  return Math.min(jump, Math.max(plain, room));
}

/**
 * The jump ceiling for the next checkpoint: the lowest level where a checkpoint
 * of this run fell below P*, and one level above the established (reference)
 * rating for players the server already knows (referenceRating > 0).
 */
export function jumpCeiling(
  checkpoints: readonly Pick<CheckpointResult, 'stats' | 'levelBefore'>[],
  referenceRating: number,
  k: AdaptiveConstants = ADAPTIVE,
): number | null {
  let ceiling: number | null = referenceRating > 0 ? levelForRating(referenceRating) + 1 : null;
  for (const c of checkpoints) {
    if (c.stats.P < k.P_TARGET && (ceiling === null || c.levelBefore < ceiling)) ceiling = c.levelBefore;
  }
  return ceiling;
}

export function evaluateCheckpoint(
  window: readonly AnswerRecord[],
  rating: number,
  level: number,
  k: AdaptiveConstants = ADAPTIVE,
  ctx: CheckpointContext = {},
): CheckpointResult {
  const stats = computeCheckpointStats(window, k);
  const ratingBefore = clampRating(rating);
  const d = normalizedDelta(stats, k);
  let delta = d >= 0 ? cappedUpDelta(d, stats.confidence, ratingBefore, ctx.ceilingLevel, k) : ratingDelta(d, stats.confidence, k);
  // Under-placed players move up at least UNDERPLACED_MIN_LEVELS levels (clearing the hysteresis margin).
  if (isUnderplaced(stats, k)) delta = Math.max(delta, ratingForLevel(level + k.UNDERPLACED_MIN_LEVELS) + k.HYSTERESIS - ratingBefore);
  const ratingAfter = clampRating(ratingBefore + delta);
  // The rail above bounds ΔR; this bounds the level step too, even when the
  // caller's rating and level disagree by more than the hysteresis margin.
  const levelAfter = Math.max(levelForRating(ratingAfter, level, k), level - k.MAX_DROP_LEVELS);
  return {
    stats,
    ratingBefore,
    ratingAfter,
    levelBefore: level,
    levelAfter,
    decision: decisionFor(level, levelAfter),
  };
}
